import { DefecationDomainService } from './domain';
import { DefecationFactory } from './entity/defecationFactory';

import type { DefecationIRepository } from './entity/defecationIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { DefecationJson, DefecationJsonPvt } from '@/types/domain/defecation/entities/defecationEntity';

/**
 * 排泄アプリケーションサービス
 */
export class DefecationApplicationService {
  private service!: DefecationDomainService;

  private defecationFactory = new DefecationFactory();

  /**
   * Injection
   * @param defecationRepository defecationRepository
   * @param logger logger
   */
  public inject(
    defecationRepository: DefecationIRepository,
    logger: Logger,
  ) {
    this.service = new DefecationDomainService(logger);
    this.service.inject(defecationRepository);
  }

  /**
   * 排泄リスト取得
   * @param token トークン
   * @returns リスト
   */
  async getDefecationList(token: string | null) {
    const result = await this.service.getDefecationList(token);
    return result;
  }

  /**
   * 指定日の排泄リスト取得
   * @param date 日付
   * @returns リスト
   */
  async getDefecationListWithDate(date: string) {
    const result = await this.service.getDefecationListWithDate(date);
    return result;
  }

  /**
   * createDefecation
   * @param input 入力
   * @returns 結果
   */
  async createDefecation(
    input: DefecationJson,
  ) {
    const entity = this.defecationFactory.createFromJson(input);
    const result = await this.service.createDefecation(entity);
    return result;
  }

  /**
   * updateDefecation
   * @param input 入力
   * @returns 結果
   */
  async updateDefecation(
    input: DefecationJson,
  ) {
    const entity = this.defecationFactory.createFromJson(input);
    const result = await this.service.updateDefecation(entity);
    return result;
  }

  /**
   * deleteDefecation
   * @param input 入力
   * @returns 結果
   */
  async deleteDefecation(
    input: DefecationJson,
  ) {
    const entity = this.defecationFactory.createFromJson(input);
    const result = await this.service.deleteDefecation(entity);
    return result;
  }

  /**
   * 排泄リストを更新する(Create)
   * @param defecationList 排泄リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createDefecationStore(
    defecationList: DefecationJson[],
    payload: EntitySubscribeType,
    filter: ((json: DefecationJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      defecationList,
      payload,
      this.defecationFactory,
      filter,
    );
  }

  /**
   * 排泄リストを更新する(Update)
   * @param defecationList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateDefecationStore(
    defecationList: DefecationJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      defecationList,
      payload,
      this.defecationFactory,
    );
  }

  /**
   * 測定リストを更新する(Delete)
   * @param defecationList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteDefecationStore(
    defecationList: DefecationJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      defecationList,
      payload,
      this.defecationFactory,
    );
  }
}
